import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { User, Mail, Cpu, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi } from "@/api";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const queryClient = useQueryClient();

  const [workerInput, setWorkerInput] = useState("");

  const { data: workerConfig } = useQuery({
    queryKey: ["worker-config"],
    queryFn: async () => {
      const res = await adminApi.getWorkerConfig();
      return res.data!;
    },
    enabled: isAdmin,
  });

  useEffect(() => {
    if (workerConfig) {
      setWorkerInput(String(workerConfig.max_workers));
    }
  }, [workerConfig]);

  const updateWorkerMutation = useMutation({
    mutationFn: (n: number) => adminApi.updateWorkerConfig(n),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["worker-config"] });
      queryClient.invalidateQueries({ queryKey: ["system-info"] });
      const data = res.data as { max_workers: number } | undefined;
      toast.success(`並行數已更新為 ${data?.max_workers ?? workerInput}`);
    },
    onError: (err: Error) => toast.error(`更新失敗: ${err.message}`),
  });

  const handleSaveWorkers = () => {
    const n = parseInt(workerInput, 10);
    if (isNaN(n) || n < 1 || n > 30) {
      toast.error("請輸入 1~30 之間的數字");
      return;
    }
    updateWorkerMutation.mutate(n);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">設定</h1>
        <p className="text-muted-foreground">帳號與系統設定</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            <CardTitle>帳號資訊</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {user?.avatar_url && (
              <img src={user.avatar_url} alt="" className="h-16 w-16 rounded-full" />
            )}
            <div className="space-y-1">
              <p className="text-lg font-medium">{user?.display_name}</p>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4" />
                <span>{user?.email}</span>
              </div>
              <Badge variant="secondary">{user?.role}</Badge>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            目前使用 Google OAuth 登入，密碼管理請至 Google 帳號設定。
          </p>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-primary" />
              <CardTitle>爬取引擎設定</CardTitle>
            </div>
            <CardDescription>
              調整 HTML 爬取的並行數量。數值越大爬取速度越快，但會佔用更多資源。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="space-y-2">
                <Label>最大並行 Workers（1~30）</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={workerInput}
                  onChange={e => setWorkerInput(e.target.value)}
                  className="w-32"
                />
              </div>
              <Button
                onClick={handleSaveWorkers}
                disabled={updateWorkerMutation.isPending || workerInput === String(workerConfig?.max_workers)}
              >
                {updateWorkerMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                儲存
              </Button>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>目前設定：<span className="font-medium text-foreground">{workerConfig?.max_workers ?? "—"}</span> 個並行爬取</p>
              <p>建議值：VPS 4 核 / 4.8 GB RAM 可設 10~20。爬取是 I/O 密集型，CPU 開銷低。</p>
              <p>注意：設太高可能觸發目標網站的 IP 封鎖。變更即時生效，無需重啟。</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
